import {
  transformInflammagingToVGraph,
  transformRichnessToVGraph,
} from '@/utils/health/transformVGraph';
import { formatTextToParagraphs } from '@/utils/string/formatTextToParagraphs';
import { GutOralDetailsCard } from '@design-system/HealthSections/GutOralDetailsCard';
import { LongevityDetailsCard } from '@design-system/HealthSections/LongevietyDetailsCard';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { Trends } from '@design-system/Trends';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import {
  InflammagingTrend,
  RichnessTrend,
} from '@viome/viome-base/src/usecase/Health/Health.model';
import React from 'react';
import EducationVideoCarousel from '../screens/HomePage/EducationVideoCarousel/EducationVideoCarousel';

interface HealthDetailPageData {
  title: string;
  about: string;
  mainStat: string;
  subStat: string;
  rankColor?: string;
  additionalStat?: string;
  videoSrc?: string;
  fallBackVdo?: any;
  overlayImg: string;
  inflammagingTrend?: InflammagingTrend[];
  richnessTrend?: RichnessTrend[];
  size: 'sm' | 'lg';
  scoreId: number;
}

interface HealthDetailPageProps {
  data: HealthDetailPageData;
  isLoading: boolean;
  isMainStatPercentage?: boolean;
  isLongvietySection?: boolean;
  journeyDeeperSectionInfo: string;
  screen: string;
}

export const HealthDetailPage: React.FC<HealthDetailPageProps> = ({
  data,
  isLoading,
  isMainStatPercentage,
  isLongvietySection,
  journeyDeeperSectionInfo,
  screen,
}) => {
  const {
    title,
    about,
    mainStat,
    subStat,
    additionalStat,
    rankColor,
    size,
    videoSrc,
    overlayImg,
    fallBackVdo,
    inflammagingTrend,
    richnessTrend,
    scoreId,
  } = data;

  return (
    <div className="dark bg-black h-full w-full">
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-12" />
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:justify-between items-center gap-4">
          {isLoading ? (
            <Skeleton className="h-10 w-80 mb-6" />
          ) : (
            <p className="typography-d2 lg:typography-d1 text-center lg:text-left lg:mt-[8rem] text-primary sm:mt-2">
              {title}
            </p>
          )}

          {isLoading ? (
            <Skeleton className="h-[24rem] w-full border border-stroke-static my-4 rounded-lg" />
          ) : isLongvietySection ? (
            <LongevityDetailsCard
              mainText={mainStat}
              isMainTextPercentage={isMainStatPercentage}
              subText={subStat}
              additionalText={additionalStat}
              subTextColor={rankColor}
              size={size}
              overlayImg={overlayImg}
              videoSrc={videoSrc}
              className="w-[28.875rem] h-[26.875rem] border-transparent"
            />
          ) : (
            <GutOralDetailsCard
              mainText={mainStat}
              isMainTextPercentage={isMainStatPercentage}
              subText={subStat}
              subTextColor={rankColor}
              videoSrc={videoSrc}
              fallBackVdo={fallBackVdo ?? ''}
              scoreId={scoreId}
            />
          )}
        </div>
        <div>
          {inflammagingTrend && inflammagingTrend.length > 1 && (
            <div className="mt-12">
              {isLoading ? (
                <Skeleton className="h-32 w-full rounded-lg" />
              ) : (
                <div>
                  <Trends
                    trend={
                      transformInflammagingToVGraph(inflammagingTrend) || []
                    }
                    topIndicator="High"
                    bottomIndicator="Low"
                    reverse
                  />
                </div>
              )}
            </div>
          )}
          {richnessTrend && richnessTrend.length > 1 && (
            <div className="mt-12">
              {isLoading ? (
                <Skeleton className="h-32 w-full rounded-lg" />
              ) : (
                <div>
                  <Trends
                    trend={transformRichnessToVGraph(richnessTrend) || []}
                    topIndicator="100"
                    bottomIndicator="0"
                    reverse
                  />
                </div>
              )}
            </div>
          )}

          <p className="dark text-primary typography-h1 pb-4 mt-4">
            {isLoading ? (
              <Skeleton className="h-6 w-1/2" />
            ) : (
              'About This Score'
            )}
          </p>
          {isLoading ? (
            <>
              <Skeleton className="h-4 mb-2" />
              <Skeleton className="h-4 mb-2" />
              <Skeleton className="h-4 mb-2" />
              <Skeleton className="h-4 mb-2" />
            </>
          ) : (
            <p className="dark typography-body text-secondary max-w-[37rem] text-justify">
              {formatTextToParagraphs(about || '')}
            </p>
          )}
        </div>
      </StandardContainer>
      <StandardContainer>
        <NutritionJourneyDeeperSection
          sectionInfo={journeyDeeperSectionInfo}
          screen={screen}
          className="mb-12"
        />
      </StandardContainer>
      {screen === 'bio_age_details' && (
        <StandardContainer>
          <div className="mt-6 mb-12">
            <EducationVideoCarousel screen="bio_age_detail" />
          </div>
        </StandardContainer>
      )}
    </div>
  );
};
